import * as React from "react";
import { useState } from "react";
import { Box, Button, IconButton, Link, Popover, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Card } from "../../../components/general/card";
import { BarTimeChart } from "../../../components/metering_tab/bar_time_chart";
import { ChartCard } from "../../../components/metering_tab/chart_card";
import { getBarTimeChartOptions, applyAutoScale } from "../../../components/metering_tab/chart_options";
import { Line } from "react-chartjs-2";
import { useParams } from "react-router-dom";
import useBackend from "../../../hooks/use_backend";
import { useQuery } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../../enums/react_query_key_enum";
import {
  fetchFinancialBeforeAfter,
  fetchFinancialCreditLine,
  downloadBeforeAfterDocument,
} from "../../../scripts/api_v2/financial_client";
import { fetchSiteProduct } from "../../../scripts/api_v2/site_client";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import dayjs from "dayjs";

export default function FinancialTab({ required_privilege }) {
  const theme = useTheme();
  const params = useParams();
  const backend = useBackend();
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  // Show 13 months: from the same month one year ago up to the selected end month
  const fromts = selectedMonth.subtract(1, "year").startOf("month").unix();
  const tots = selectedMonth.endOf("month").unix();

  // Credit line: only the selected month
  const creditFromts = selectedMonth.startOf("month").unix();

  const { data = [] } = useQuery({
    queryKey: [REACT_QUERY_KEYS.FINANCIAL_BEFORE_AFTER, backend.fetch, params.site_id, fromts],
    queryFn: () => fetchFinancialBeforeAfter(backend.fetch, params.site_id, fromts, tots),
  });

  const { data: creditLineData = [] } = useQuery({
    queryKey: [REACT_QUERY_KEYS.FINANCIAL_CREDIT_LINE, backend.fetch, params.site_id, creditFromts],
    queryFn: () => fetchFinancialCreditLine(backend.fetch, params.site_id, creditFromts, tots),
  });

  const { data: product } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_PRODUCT, backend.fetch, params.site_id],
    queryFn: () => fetchSiteProduct(backend.fetch, params.site_id),
  });

  const isEpexSteering = product?.conf_optimizer === "EPEX";

  const handleDownloadDocument = async () => {
    if (!isDownloadingPdf) {
      setIsDownloadingPdf(true);
      try {
        await downloadBeforeAfterDocument(backend.fetch);
      } catch (e) {
        console.error(e);
      } finally {
        setIsDownloadingPdf(false);
      }
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} padding={2}>
      <HeaderCard selectedMonth={selectedMonth} setSelectedMonth={setSelectedMonth} />
      <ChartCard
        Chart={<BeforeAfterInvoicesChart data={data} />}
        title="Before vs After"
        subtitle={
          <>
            The before vs after visualisation is a simulation of the before and after invoices, without the Elion
            control compared with the effective invoice including the Elion control.{" "}
            <Link
              component="button"
              variant="body2"
              onClick={handleDownloadDocument}
              disabled={isDownloadingPdf}
              sx={{
                cursor: isDownloadingPdf ? "wait" : "pointer",
                textDecoration: "underline",
                fontSize: "inherit",
                verticalAlign: "baseline",
                fontWeight: "inherit",
                opacity: isDownloadingPdf ? 0.6 : 1,
                "&:disabled": {
                  textDecoration: "underline",
                },
              }}
            >
              Click here for more information.
            </Link>
          </>
        }
        info="The before-and-after visualization concerns a purely indicative and non-binding simulation of your energy bill without the application of Elion control, compared to your actual bill including Elion control. No rights can be derived from the results displayed. Elindus accepts no liability for any differences between the simulated and actual costs or savings."
        data={data}
      />
      {isEpexSteering ? (
        <ChartCard
          title="Credit Line"
          subtitle="The flexibility compensation is a profit generated on the imbalance market and is not applicable for EPEX-steered sites."
          noDataMessage={" "} // Prevent "No data" message from showing, to avoid confusion with the fact that there is simply no credit line for EPEX-steered sites
        />
      ) : (
        <ChartCard
          Chart={<CreditLineChart data={creditLineData} />}
          title="Credit Line"
          subtitle="This compensation is generated by controlling on the imbalance market and can be found on your Elindus invoice."
          data={creditLineData}
        />
      )}
    </Box>
  );
}

function HeaderCard({ selectedMonth, setSelectedMonth }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const today = dayjs();
  const nextDisabled = selectedMonth.isSame(today, "month") || selectedMonth.isAfter(today, "month");

  return (
    <Card display="flex" flexDirection="column" justifyContent="center" alignItems="center">
      <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
        <IconButton sx={{ width: 40, height: 40 }} onClick={() => setSelectedMonth(selectedMonth.subtract(1, "month"))}>
          <ArrowLeftIcon />
        </IconButton>
        <Button
          style={{ fontWeight: "bold", fontSize: 20 }}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          startIcon={<CalendarMonthIcon />}
        >
          {selectedMonth.format("MMMM YYYY")}
        </Button>
        <IconButton
          sx={{ width: 40, height: 40 }}
          onClick={() => setSelectedMonth(selectedMonth.add(1, "month"))}
          disabled={nextDisabled}
        >
          <ArrowRightIcon />
        </IconButton>
        <Popover
          open={anchorEl != null}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <DateCalendar
            views={["year", "month"]}
            openTo="month"
            value={selectedMonth}
            format="MMM/YYYY"
            maxDate={today}
            onChange={(newValue) => {
              setSelectedMonth(newValue ?? today);
              setAnchorEl(null);
            }}
          />
        </Popover>
      </Box>
    </Card>
  );
}

export function BeforeAfterInvoicesChart(props) {
  const theme = useTheme();
  // Filter out months where either value is 0
  const filteredData = props.data.filter((el) => el.before_client_total !== 0 && el.after_total !== 0);

  const beforeData = filteredData.map((el) =>
    el.before_client_total === null ? null : Math.round(el.before_client_total * 100) / 100,
  );
  const afterData = filteredData.map((el) => (el.after_total === null ? null : Math.round(el.after_total * 100) / 100));

  const chartData = {
    labels: filteredData.map((el) => new Date(1000 * el.period)),
    datasets: [
      {
        fill: true,
        label: "Before",
        data: beforeData,
        backgroundColor: theme.palette.grey[400],
        cubicInterpolationMode: "monotone",
        pointStyle: false,
        order: 1,
        minBarLength: 1,
      },
      {
        fill: true,
        label: "After",
        data: afterData,
        backgroundColor: theme.palette.primary.main,
        cubicInterpolationMode: "monotone",
        pointStyle: false,
        order: 2,
        minBarLength: 1,
      },
    ],
  };

  const options = getBarTimeChartOptions(true);
  options.scales.x.time.unit = "month";
  options.scales.x.time.tooltipFormat = "MMM yyyy";
  options.scales.x.time.displayFormats = { month: "MMM yyyy" };
  options.scales.x.ticks = { source: "labels" };
  options.scales.x.stacked = false;
  options.scales.x.offset = true;
  options.scales.y.stacked = false;

  const { unit, divisor } = applyAutoScale(options, [beforeData, afterData], "€");
  options.plugins.tooltip = {
    ...options.plugins.tooltip,
    callbacks: {
      label: function (tooltipItem) {
        const value = tooltipItem.raw;
        if (value === null || value === undefined) return null;
        return `${tooltipItem.dataset.label}: ${(value / divisor).toFixed(2)} ${unit}`;
      },
    },
  };

  return <BarTimeChart data={chartData} options={options} />;
}

export function CreditLineChart(props) {
  const theme = useTheme();

  const creditData = props.data.map((el) => el.fct_fract_100);

  const chartData = {
    labels: props.data.map((el) => new Date(1000 * el.utctime)),
    datasets: [
      {
        fill: true,
        label: "Credit Line",
        data: creditData,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, context.chart.height);
          gradient.addColorStop(0, alpha(theme.palette.primary.main, 0.9));
          gradient.addColorStop(1, alpha(theme.palette.primary.main, 0.1));
          return gradient;
        },
        borderWidth: 2,
        borderColor: theme.palette.primary.main,
        cubicInterpolationMode: "monotone",
        pointStyle: false,
        order: 2,
        minBarLength: 1,
      },
    ],
  };

  const options = getBarTimeChartOptions(true);
  options.interaction = { mode: "nearest", intersect: false };
  options.scales.x.time.unit = "day";
  options.scales.x.time.tooltipFormat = "dd/MM/yyyy HH:mm";
  options.scales.x.time.displayFormats = { day: "MMM dd" };
  options.scales.x.stacked = false;
  options.scales.y.stacked = false;
  options.scales.y.title = { display: true, text: "€" };
  options.plugins.legend.display = false;

  return <Line data={chartData} width={"100%"} options={options} />;
}
